import React, { useState, useEffect } from 'react';
import config from '../../config';
import './ItemsManagement.css';

interface Item {
  id: string;
  name: string;
  description?: string;
  category: string;
  subcategory?: string;
  baseUnit: string;
  allergenInfo: string[];
  nutritionalInfo?: any;
  storageRequirements?: any;
  shelfLife?: number;
  isActive: boolean;
  createdAt: string;
  supplierItems?: any[];
}

interface ItemsManagementProps {
  selectedStore: string;
  enterpriseId: string;
  user: any;
}

const ItemsManagement: React.FC<ItemsManagementProps> = ({ selectedStore, enterpriseId, user }) => {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    subcategory: '',
    baseUnit: '',
    allergenInfo: [] as string[],
    nutritionalInfo: {
      calories: '',
      protein: '',
      fat: '',
      carbohydrates: '',
      fiber: '',
      sodium: ''
    },
    storageRequirements: {
      temperature: '',
      humidity: '',
      notes: ''
    },
    shelfLife: ''
  });

  const categories = [
    'protein', 'produce', 'dairy', 'dry_goods', 'beverages', 'supplies', 
    'spices', 'oils', 'frozen', 'bakery', 'prepared_foods'
  ];

  const units = [
    'lb', 'oz', 'kg', 'g', 'gallon', 'quart', 'liter', 'ml', 'each', 
    'dozen', 'case', 'box', 'bag', 'can', 'bottle'
  ];

  const allergens = [
    'milk', 'eggs', 'fish', 'shellfish', 'tree_nuts', 'peanuts', 
    'wheat', 'soybeans', 'sesame'
  ];

  const temperatureOptions = [
    'frozen', 'refrigerated', 'cool_dry', 'room_temperature', 'warm'
  ];

  useEffect(() => {
    if (enterpriseId) {
      fetchItems();
    }
  }, [enterpriseId]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.API_BASE_URL}/inventory/items/${enterpriseId}`);
      
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      } else {
        console.error('Failed to fetch items');
        setItems([]);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        allergenInfo: formData.allergenInfo,
        nutritionalInfo: Object.entries(formData.nutritionalInfo).reduce((acc, [key, value]) => {
          if (value) acc[key] = parseFloat(value as string) || value;
          return acc;
        }, {} as any),
        shelfLife: formData.shelfLife ? parseInt(formData.shelfLife) : null,
        enterpriseId
      };

      const url = editingItem 
        ? `${config.API_BASE_URL}/inventory/items/${editingItem.id}`
        : `${config.API_BASE_URL}/inventory/items`;
      
      const method = editingItem ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        await fetchItems();
        resetForm();
        setShowForm(false);
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Error saving item');
    }
  };

  const handleEdit = (item: Item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      category: item.category,
      subcategory: item.subcategory || '',
      baseUnit: item.baseUnit,
      allergenInfo: item.allergenInfo || [],
      nutritionalInfo: {
        calories: item.nutritionalInfo?.calories?.toString() || '',
        protein: item.nutritionalInfo?.protein?.toString() || '',
        fat: item.nutritionalInfo?.fat?.toString() || '',
        carbohydrates: item.nutritionalInfo?.carbohydrates?.toString() || '',
        fiber: item.nutritionalInfo?.fiber?.toString() || '',
        sodium: item.nutritionalInfo?.sodium?.toString() || ''
      },
      storageRequirements: {
        temperature: item.storageRequirements?.temperature || '',
        humidity: item.storageRequirements?.humidity || '',
        notes: item.storageRequirements?.notes || ''
      },
      shelfLife: item.shelfLife?.toString() || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (item: Item) => {
    if (window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
      try {
        const response = await fetch(`${config.API_BASE_URL}/inventory/items/${item.id}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          await fetchItems();
        } else {
          alert('Error deleting item');
        }
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Error deleting item');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      subcategory: '',
      baseUnit: '',
      allergenInfo: [],
      nutritionalInfo: {
        calories: '',
        protein: '',
        fat: '',
        carbohydrates: '',
        fiber: '',
        sodium: ''
      },
      storageRequirements: {
        temperature: '',
        humidity: '',
        notes: ''
      },
      shelfLife: ''
    });
    setEditingItem(null);
  };

  const handleAllergenChange = (allergen: string, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        allergenInfo: [...prev.allergenInfo, allergen]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        allergenInfo: prev.allergenInfo.filter(a => a !== allergen)
      }));
    }
  };

  const getCategories = () => {
    const categorySet = new Set(items.map(item => item.category));
    const uniqueCategories = Array.from(categorySet);
    return uniqueCategories.sort();
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = !categoryFilter || item.category === categoryFilter;
    return matchesSearch && matchesCategory && item.isActive;
  });

  if (loading) {
    return (
      <div className="items-loading">
        <div className="loading-spinner"></div>
        <p>Loading items...</p>
      </div>
    );
  }

  return (
    <div className="items-management">
      {/* Header */}
      <div className="items-header">
        <div className="header-info">
          <h2>Items Catalog Management</h2>
          <p>Manage your master item catalog with categories, allergens, and specifications.</p>
        </div>
        <button 
          className="add-item-btn" 
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          ➕ Add New Item
        </button>
      </div>

      {/* Filters */}
      <div className="items-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">All Categories</option>
          {getCategories().map(category => (
            <option key={category} value={category}>
              {category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      {/* Items List */}
      <div className="items-list">
        {filteredItems.length === 0 ? (
          <div className="empty-items">
            <h3>No Items Found</h3>
            <p>Start by adding items to your catalog.</p>
            <button 
              className="add-first-item-btn"
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
            >
              Add Your First Item
            </button>
          </div>
        ) : (
          <div className="items-grid">
            {filteredItems.map((item) => (
              <div key={item.id} className="item-card">
                <div className="item-header">
                  <h3 className="item-name">{item.name}</h3>
                  <span className={`category-badge ${item.category}`}>
                    {item.category.replace('_', ' ')}
                  </span>
                </div>
                
                {item.description && (
                  <p className="item-description">{item.description}</p>
                )}
                
                <div className="item-details">
                  <div className="detail-row">
                    <span className="label">Unit:</span>
                    <span className="value">{item.baseUnit}</span>
                  </div>
                  
                  {item.subcategory && (
                    <div className="detail-row">
                      <span className="label">Subcategory:</span>
                      <span className="value">{item.subcategory}</span>
                    </div>
                  )}
                  
                  {item.shelfLife && (
                    <div className="detail-row">
                      <span className="label">Shelf Life:</span>
                      <span className="value">{item.shelfLife} days</span>
                    </div>
                  )}
                  
                  {item.allergenInfo && item.allergenInfo.length > 0 && (
                    <div className="detail-row">
                      <span className="label">Allergens:</span>
                      <div className="allergen-tags">
                        {item.allergenInfo.map(allergen => (
                          <span key={allergen} className="allergen-tag">
                            {allergen.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {item.supplierItems && item.supplierItems.length > 0 && (
                    <div className="detail-row">
                      <span className="label">Suppliers:</span>
                      <span className="value">{item.supplierItems.length} configured</span>
                    </div>
                  )}
                </div>

                <div className="item-actions">
                  <button 
                    className="edit-btn"
                    onClick={() => handleEdit(item)}
                  >
                    Edit
                  </button>
                  <button 
                    className="delete-btn"
                    onClick={() => handleDelete(item)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="form-overlay">
          <div className="form-modal">
            <div className="form-header">
              <h3>{editingItem ? 'Edit Item' : 'Add New Item'}</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="item-form">
              {/* Basic Information */}
              <div className="form-section">
                <h4>Basic Information</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Item Name *</label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      placeholder="e.g., Ground Beef 80/20"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="baseUnit">Base Unit *</label>
                    <select
                      id="baseUnit"
                      value={formData.baseUnit}
                      onChange={(e) => setFormData(prev => ({ ...prev, baseUnit: e.target.value }))}
                      required
                    >
                      <option value="">Select unit</option>
                      {units.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    placeholder="Optional description..."
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="category">Category *</label>
                    <select
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      required
                    >
                      <option value="">Select category</option>
                      {categories.map(category => (
                        <option key={category} value={category}>
                          {category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="subcategory">Subcategory</label>
                    <input
                      type="text"
                      id="subcategory"
                      value={formData.subcategory}
                      onChange={(e) => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}
                      placeholder="e.g., beef, chicken, vegetables"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="shelfLife">Shelf Life (days)</label>
                  <input
                    type="number"
                    id="shelfLife"
                    value={formData.shelfLife}
                    onChange={(e) => setFormData(prev => ({ ...prev, shelfLife: e.target.value }))}
                    min="1"
                    placeholder="Days before expiration"
                  />
                </div>
              </div>

              {/* Allergen Information */}
              <div className="form-section">
                <h4>Allergen Information</h4>
                <div className="allergen-checkboxes">
                  {allergens.map(allergen => (
                    <label key={allergen} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.allergenInfo.includes(allergen)}
                        onChange={(e) => handleAllergenChange(allergen, e.target.checked)}
                      />
                      {allergen.charAt(0).toUpperCase() + allergen.slice(1).replace('_', ' ')}
                    </label>
                  ))}
                </div>
              </div>

              {/* Nutritional Information */}
              <div className="form-section">
                <h4>Nutritional Information (per base unit)</h4>
                <div className="nutrition-grid">
                  <div className="form-group">
                    <label>Calories</label>
                    <input
                      type="number"
                      value={formData.nutritionalInfo.calories}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        nutritionalInfo: { ...prev.nutritionalInfo, calories: e.target.value }
                      }))}
                      step="0.1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Protein (g)</label>
                    <input
                      type="number"
                      value={formData.nutritionalInfo.protein}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        nutritionalInfo: { ...prev.nutritionalInfo, protein: e.target.value }
                      }))}
                      step="0.1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Fat (g)</label>
                    <input
                      type="number"
                      value={formData.nutritionalInfo.fat}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        nutritionalInfo: { ...prev.nutritionalInfo, fat: e.target.value }
                      }))}
                      step="0.1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Carbs (g)</label>
                    <input
                      type="number"
                      value={formData.nutritionalInfo.carbohydrates}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        nutritionalInfo: { ...prev.nutritionalInfo, carbohydrates: e.target.value }
                      }))}
                      step="0.1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Fiber (g)</label>
                    <input
                      type="number"
                      value={formData.nutritionalInfo.fiber}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        nutritionalInfo: { ...prev.nutritionalInfo, fiber: e.target.value }
                      }))}
                      step="0.1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Sodium (mg)</label>
                    <input
                      type="number"
                      value={formData.nutritionalInfo.sodium}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        nutritionalInfo: { ...prev.nutritionalInfo, sodium: e.target.value }
                      }))}
                      step="0.1"
                    />
                  </div>
                </div>
              </div>

              {/* Storage Requirements */}
              <div className="form-section">
                <h4>Storage Requirements</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="temperature">Temperature</label>
                    <select
                      id="temperature"
                      value={formData.storageRequirements.temperature}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        storageRequirements: { ...prev.storageRequirements, temperature: e.target.value }
                      }))}
                    >
                      <option value="">Select temperature</option>
                      {temperatureOptions.map(temp => (
                        <option key={temp} value={temp}>
                          {temp.charAt(0).toUpperCase() + temp.slice(1).replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="humidity">Humidity</label>
                    <input
                      type="text"
                      id="humidity"
                      value={formData.storageRequirements.humidity}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        storageRequirements: { ...prev.storageRequirements, humidity: e.target.value }
                      }))}
                      placeholder="e.g., low, medium, high"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="storageNotes">Storage Notes</label>
                  <textarea
                    id="storageNotes"
                    value={formData.storageRequirements.notes}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      storageRequirements: { ...prev.storageRequirements, notes: e.target.value }
                    }))}
                    rows={2}
                    placeholder="Special storage instructions..."
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  {editingItem ? 'Update Item' : 'Create Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemsManagement;